import { useEffect, useRef, useState } from 'react';
import { randomUUID, type ProjectModel } from '@video-studio/shared';
import { ApiFoutException } from '../../api/client';
import type { useEditor } from '../../state/useEditor';

/**
 * Chatpaneel.
 *
 * Een gewone opdracht wijzigt alleen de tijdlijn. Een https-link naar een
 * productpagina haalt de foto’s en USP’s op en bouwt daar een video van.
 * Dat maakt geen AI-videobeeld en kost geen videokosten.
 *
 * Elke toepassing komt in de undo-geschiedenis van de editor, dus één klik op
 * "Ongedaan" draait de tijdlijn terug. De gedownloade foto’s blijven in de
 * bibliotheek staan.
 */
export interface ChatPaneelProps {
  editor: ReturnType<typeof useEditor>;
}

interface ToegepasteActie {
  actie: string;
  beschrijving: string;
  gelukt: boolean;
  fout?: string;
}

interface ChatAntwoord {
  bron: 'model' | 'lokaal';
  toelichting: string;
  onduidelijk: boolean;
  verduidelijkingsvraag?: string;
  acties: unknown[];
  toegepast: ToegepasteActie[];
  mislukt: ToegepasteActie[];
  versie?: number | null;
  project?: ProjectModel | null;
  voorstel?: ProjectModel | null;
}

interface PaginaPlan {
  url: string;
  naam: string;
  merk: string;
  hook: string;
  usps: string[];
  beelden: Array<{ url: string; naam: string }>;
  duurSeconden: number;
  toelichting: string;
  ai?: { resolutie: string; ramingUsd: number; clips: number };
}

interface Bericht {
  id: string;
  rol: 'gebruiker' | 'assistent';
  tekst: string;
  antwoord?: ChatAntwoord;
  pagina?: PaginaPlan;
  paginaGemaakt?: boolean;
  bezig?: boolean;
}

const PRODUCTVOORBEELD =
  'Maak een mooie video van 15 seconden van https://www.evergetikon.eu/nl-nl/products/laurier-olie-evergetikon met de USP’s';

const VOORBEELDOPDRACHTEN = [
  PRODUCTVOORBEELD,
  'Kort deze video in tot 20 seconden',
  'Zet het logo rechtsboven',
  "Voeg aan het einde de tekst 'Bekijk ons assortiment' toe",
  'Maak een verticale versie',
  'Zet de muziek zachter',
];

export function ChatPaneel({ editor }: ChatPaneelProps) {
  const [berichten, setBerichten] = useState<Bericht[]>([]);
  const [invoer, setInvoer] = useState('');
  const [bezig, setBezig] = useState(false);
  const [catalogus, setCatalogus] = useState<{ acties: Array<{ actie: string; uitleg: string }>; taalmodel: { geconfigureerd: boolean; toelichting: string } } | null>(null);
  const [toonCatalogus, setToonCatalogus] = useState(false);
  const lijstRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void fetch('/v1/ai/montage/catalogus')
      .then((r) => r.json())
      .then(setCatalogus)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    lijstRef.current?.scrollTo({ top: lijstRef.current.scrollHeight });
  }, [berichten]);

  const verstuur = async (opdracht: string) => {
    if (!editor.project || !opdracht.trim()) return;
    const gebruikersBericht: Bericht = {
      id: randomUUID(),
      rol: 'gebruiker',
      tekst: opdracht.trim(),
    };
    const tijdelijkId = randomUUID();
    setBerichten((huidig) => [
      ...huidig,
      gebruikersBericht,
      { id: tijdelijkId, rol: 'assistent', tekst: '', bezig: true },
    ]);
    setInvoer('');
    setBezig(true);

    try {
      const isProductpagina = /https:\/\//i.test(opdracht);
      const respons = await fetch(isProductpagina ? '/v1/ai/paginavideo' : '/v1/ai/montage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: editor.project.id,
          opdracht: opdracht.trim(),
          modus: 'voorstel',
        }),
      });
      const data = await respons.json();
      if (!respons.ok) {
        throw new ApiFoutException(
          respons.status,
          data?.fout?.code ?? 'MONTAGE',
          data?.fout?.melding ?? 'De opdracht kon niet worden verwerkt.',
        );
      }
      if (isProductpagina) {
        const plan = data.plan as PaginaPlan | undefined;
        if (!plan?.toelichting) {
          throw new ApiFoutException(502, 'PAGINA', 'De pagina gaf geen bruikbaar plan terug.');
        }
        const ai = data.ai as { resolutie?: string; ramingUsd?: number; clips?: unknown[] } | undefined;
        if (ai && typeof ai.ramingUsd === 'number') {
          plan.ai = {
            resolutie: ai.resolutie ?? '1080p',
            ramingUsd: ai.ramingUsd,
            clips: Array.isArray(ai.clips) ? ai.clips.length : 1,
          };
        }
        setBerichten((huidig) =>
          huidig.map((b) =>
            b.id === tijdelijkId
              ? { ...b, bezig: false, tekst: plan.toelichting, pagina: plan }
              : b,
          ),
        );
      } else {
        const antwoord = data as ChatAntwoord;
        setBerichten((huidig) =>
          huidig.map((b) =>
            b.id === tijdelijkId
              ? { ...b, bezig: false, tekst: antwoord.toelichting, antwoord }
              : b,
          ),
        );
      }
    } catch (fout) {
      // Een fout moet zichtbaar zijn, niet stil verdwijnen in de console. Dit
      // is de reden dat een storing in een niet-beveiligde context (waarin
      // sommige browser-API's ontbreken) meteen opvalt.
      const melding =
        fout instanceof ApiFoutException
          ? fout.message
          : fout instanceof Error
            ? `Er ging iets mis: ${fout.message}`
            : 'Er ging iets mis.';
      setBerichten((huidig) =>
        huidig.map((b) =>
          b.id === tijdelijkId ? { ...b, bezig: false, tekst: melding } : b,
        ),
      );
    } finally {
      setBezig(false);
    }
  };

  const toepassen = async (berichtId: string, antwoord: ChatAntwoord, opdracht: string) => {
    if (!editor.project) return;
    setBezig(true);
    try {
      const respons = await fetch('/v1/ai/montage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: editor.project.id,
          opdracht,
          modus: 'toepassen',
          verwachteVersie: editor.versie,
          // De acties die de gebruiker te zien kreeg, worden opnieuw gevalideerd
          // en toegepast. Het taalmodel wordt niet opnieuw gevraagd, zodat er
          // nooit iets anders gebeurt dan wat er op het scherm stond.
          acties: antwoord.acties,
        }),
      });
      const data = await respons.json();
      if (!respons.ok) {
        throw new ApiFoutException(
          respons.status,
          data?.fout?.code ?? 'MONTAGE',
          data?.fout?.melding ?? 'Toepassen mislukt.',
        );
      }
      const resultaat = data as ChatAntwoord & { project: ProjectModel };
      if (resultaat.project) {
        // Via `overschrijf` komt de wijziging in de undo-geschiedenis.
        editor.overschrijf(resultaat.project, `Opdracht: ${antwoord.toelichting.slice(0, 60)}`);
      }
      setBerichten((huidig) =>
        huidig.map((b) =>
          b.id === berichtId
            ? {
                ...b,
                tekst: `${b.tekst}\n\nToegepast: ${resultaat.toegepast.map((t) => t.beschrijving).join('; ')}${
                  resultaat.mislukt.length > 0
                    ? `\nNiet gelukt: ${resultaat.mislukt.map((t) => `${t.beschrijving} (${t.fout})`).join('; ')}`
                    : ''
                }`,
                antwoord: { ...b.antwoord!, toegepast: resultaat.toegepast, mislukt: resultaat.mislukt },
              }
            : b,
        ),
      );
    } catch (fout) {
      setBerichten((huidig) => [
        ...huidig,
        {
          id: randomUUID(),
          rol: 'assistent',
          tekst: fout instanceof Error ? fout.message : 'Toepassen mislukt.',
        },
      ]);
    } finally {
      setBezig(false);
    }
  };

  const maakPagina = async (berichtId: string, plan: PaginaPlan) => {
    if (!editor.project) return;
    setBezig(true);
    try {
      const respons = await fetch('/v1/ai/paginavideo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: editor.project.id,
          opdracht: plan.url,
          modus: 'maken',
          plan,
        }),
      });
      const data = await respons.json();
      if (!respons.ok) {
        throw new ApiFoutException(
          respons.status,
          data?.fout?.code ?? 'PAGINA',
          data?.fout?.melding ?? 'De video kon niet worden gemaakt.',
        );
      }
      const project = data.project as ProjectModel | null;
      if (project) {
        editor.overschrijf(project, `Productvideo: ${plan.naam}`);
        await editor.herlaadMedia();
      }
      setBerichten((huidig) =>
        huidig.map((b) =>
          b.id === berichtId
            ? {
                ...b,
                paginaGemaakt: true,
                tekst: `${plan.toelichting}\n\nDe video staat op de tijdlijn. De foto’s staan in de bibliotheek.`,
              }
            : b,
        ),
      );
    } catch (fout) {
      setBerichten((huidig) => [
        ...huidig,
        {
          id: randomUUID(),
          rol: 'assistent',
          tekst: fout instanceof Error ? fout.message : 'De video kon niet worden gemaakt.',
        },
      ]);
    } finally {
      setBezig(false);
    }
  };

  const zetTekst = (berichtId: string, tekst: string) => {
    setBerichten((huidig) => huidig.map((b) => (b.id === berichtId ? { ...b, tekst } : b)));
  };

  const maakMetAi = async (berichtId: string, plan: PaginaPlan) => {
    if (!editor.project) return;
    setBezig(true);
    zetTekst(berichtId, `Grok maakt bewegende beelden van de foto’s. Geschatte kosten: $${plan.ai?.ramingUsd.toFixed(2) ?? '?'}.`);
    try {
      const start = await fetch('/v1/ai/paginavideo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: editor.project.id,
          opdracht: plan.url,
          modus: 'ai',
          plan,
        }),
      });
      const gestart = await start.json();
      if (!start.ok) {
        throw new ApiFoutException(
          start.status,
          gestart?.fout?.code ?? 'AI',
          gestart?.fout?.melding ?? 'De AI-video kon niet worden gestart.',
        );
      }
      const ids = (gestart.generaties as Array<{ id: string }> | undefined)?.map((g) => g.id) ?? [];
      if (ids.length === 0) throw new ApiFoutException(502, 'AI', 'Er is geen generatie gestart.');

      const einde = Date.now() + 15 * 60 * 1000;
      let laatste = '';
      while (Date.now() < einde) {
        const statussen = await Promise.all(
          ids.map(async (id) => {
            const respons = await fetch(`/v1/ai/generaties/${id}`);
            const data = await respons.json();
            if (!respons.ok) {
              throw new ApiFoutException(respons.status, data?.fout?.code ?? 'AI', data?.fout?.melding ?? 'Status ophalen mislukt.');
            }
            return data as { status: string; foutMelding?: string | null };
          }),
        );
        const af = statussen.filter((s) => s.status === 'klaar' || s.status === 'mislukt' || s.status === 'onduidelijk');
        const tekst = `Grok maakt de beelden (${af.length} van ${statussen.length} af)…`;
        if (tekst !== laatste) {
          zetTekst(berichtId, tekst);
          laatste = tekst;
        }
        if (af.length === statussen.length) {
          const mislukt = statussen.find((s) => s.status !== 'klaar');
          if (mislukt && statussen.every((s) => s.status !== 'klaar')) {
            throw new ApiFoutException(502, 'AI', mislukt.foutMelding || 'De AI-video is niet gelukt.');
          }
          break;
        }
        await new Promise((wacht) => window.setTimeout(wacht, 4000));
      }

      const plaats = await fetch('/v1/ai/paginavideo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: editor.project.id,
          opdracht: plan.url,
          modus: 'ai-plaats',
          plan,
          generatieIds: ids,
        }),
      });
      const data = await plaats.json();
      if (!plaats.ok) {
        throw new ApiFoutException(plaats.status, data?.fout?.code ?? 'AI', data?.fout?.melding ?? 'De AI-video kon niet op de tijdlijn.');
      }
      if (!data.klaar || !data.project) {
        throw new ApiFoutException(502, 'AI', 'De generatie duurde te lang. De beelden komen in de bibliotheek zodra Grok klaar is.');
      }
      editor.overschrijf(data.project as ProjectModel, `AI-productvideo: ${plan.naam}`);
      await editor.herlaadMedia();
      setBerichten((huidig) =>
        huidig.map((b) =>
          b.id === berichtId
            ? {
                ...b,
                paginaGemaakt: true,
                tekst: `${plan.toelichting}\n\nDe AI-video staat op de tijdlijn. De clips staan in de bibliotheek.`,
              }
            : b,
        ),
      );
    } catch (fout) {
      setBerichten((huidig) => [
        ...huidig,
        {
          id: randomUUID(),
          rol: 'assistent',
          tekst: fout instanceof Error ? fout.message : 'De AI-video kon niet worden gemaakt.',
        },
      ]);
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="chatpaneel">
      <div className="chatpaneel__kop">
        <div>
          <h3>Montage via een opdracht</h3>
          <p className="paneel__hint">
            Een productlink wordt een video van de foto’s en USP’s van die pagina. Je kiest daarna
            of de foto’s blijven staan, of Grok er bewegende beelden van maakt. De prijs van die
            AI-beelden staat op de knop. Andere opdrachten wijzigen alleen de tijdlijn.
          </p>
        </div>
        <button
          type="button"
          className="knop knop--klein knop--stil"
          onClick={() => setToonCatalogus((v) => !v)}
          title="Bekijk welke acties een taalmodel mag uitvoeren"
        >
          {toonCatalogus ? 'Verberg regels' : 'Toegestane acties'}
        </button>
      </div>

      {catalogus && !catalogus.taalmodel.geconfigureerd && (
        <div className="melding melding--info melding--klein">
          Er is geen taalmodel ingesteld (GEMINI_API_KEY ontbreekt). De ingebouwde opdrachten
          hieronder werken wel; vrije opdrachten worden dan niet herkend.
        </div>
      )}

      {toonCatalogus && catalogus && (
        <div className="chatpaneel__catalogus">
          <p className="paneel__hint">
            Meer kan een taalmodel niet doen. Onbekende acties worden geweigerd voordat ze het
            project raken.
          </p>
          <ul>
            {catalogus.acties.map((actie) => (
              <li key={actie.actie}>
                <code>{actie.actie}</code> — {actie.uitleg}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="chatpaneel__lijst" ref={lijstRef}>
        {berichten.length === 0 && (
          <div className="chatpaneel__leeg">
            <p>Probeer een van deze opdrachten:</p>
            <div className="chatpaneel__voorbeelden">
              {VOORBEELDOPDRACHTEN.map((voorbeeld) => (
                <button
                  key={voorbeeld}
                  type="button"
                  className="knop knop--klein"
                  onClick={() => void verstuur(voorbeeld)}
                >
                  {voorbeeld}
                </button>
              ))}
            </div>
          </div>
        )}

        {berichten.map((bericht) => (
          <div
            key={bericht.id}
            className={`chatbericht chatbericht--${bericht.rol}`}
          >
            <div className="chatbericht__tekst">
              {bericht.bezig ? <span className="chatbericht__bezig">Bezig…</span> : bericht.tekst}
            </div>

            {bericht.pagina && !bericht.paginaGemaakt && (
              <div className="paginaplan">
                <strong>{bericht.pagina.naam}</strong>
                <p>{bericht.pagina.hook}</p>
                {bericht.pagina.usps.length > 0 && (
                  <ul>
                    {bericht.pagina.usps.map((usp) => (
                      <li key={usp}>{usp}</li>
                    ))}
                  </ul>
                )}
                <span className="chatbericht__hint">
                  {bericht.pagina.beelden.length} foto’s · {bericht.pagina.duurSeconden} seconden
                  {bericht.pagina.ai
                    ? ` · AI-video ongeveer $${bericht.pagina.ai.ramingUsd.toFixed(2)} (${bericht.pagina.ai.resolutie})`
                    : ''}
                </span>
                <button
                  type="button"
                  className="knop knop--klein"
                  disabled={bezig}
                  onClick={() => void maakPagina(bericht.id, bericht.pagina!)}
                >
                  Maak met de foto’s
                </button>
                {bericht.pagina.ai && (
                  <button
                    type="button"
                    className="knop knop--primair knop--klein"
                    disabled={bezig}
                    onClick={() => void maakMetAi(bericht.id, bericht.pagina!)}
                  >
                    Maak met AI · ${bericht.pagina.ai.ramingUsd.toFixed(2)}
                  </button>
                )}
              </div>
            )}

            {bericht.antwoord && bericht.antwoord.acties.length > 0 && (
              <div className="chatbericht__acties">
                <strong>Voorstel ({bericht.antwoord.bron === 'model' ? 'taalmodel' : 'ingebouwde regels'}):</strong>
                <ul>
                  {bericht.antwoord.acties.map((actie, index) => (
                    <li key={index}>
                      <code>{(actie as { actie: string }).actie}</code>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="knop knop--primair knop--klein"
                  disabled={bezig}
                  onClick={() => {
                    // De oorspronkelijke opdracht hoort bij dit antwoord; die
                    // staat in het gebruikersbericht direct ervoor.
                    const index = berichten.findIndex((b) => b.id === bericht.id);
                    const opdracht = berichten[index - 1]?.tekst ?? bericht.tekst;
                    void toepassen(bericht.id, bericht.antwoord!, opdracht);
                  }}
                >
                  Toepassen op de tijdlijn
                </button>
                <span className="chatbericht__hint">Daarna één klik op "Ongedaan" draait het terug.</span>
              </div>
            )}

            {bericht.antwoord?.onduidelijk && bericht.antwoord.verduidelijkingsvraag && (
              <div className="chatbericht__vraag">{bericht.antwoord.verduidelijkingsvraag}</div>
            )}

            {bericht.antwoord && bericht.antwoord.mislukt.length > 0 && (
              <div className="chatbericht__mislukt">
                Niet toegepast:
                <ul>
                  {bericht.antwoord.mislukt.map((actie, index) => (
                    <li key={index}>
                      {actie.beschrijving} — {actie.fout}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      <form
        className="chatpaneel__invoer"
        onSubmit={(e) => {
          e.preventDefault();
          void verstuur(invoer);
        }}
      >
        <textarea
          rows={2}
          value={invoer}
          placeholder="Bijvoorbeeld: maak een video van 15 seconden van https://…"
          onChange={(e) => setInvoer(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void verstuur(invoer);
            }
          }}
        />
        <button type="submit" className="knop knop--primair" disabled={bezig || !invoer.trim()}>
          Vraag
        </button>
      </form>
    </div>
  );
}
